import React from 'react';
import PropTypes from 'prop-types';
import {
  withStyles,
  FormControl,
  Icon,
  Tooltip,
  Typography,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import {
  textType,
  numberType,
  dateType,
  dateTimeType,
  userType,
  addressType,
  emailType,
  phoneType,
  yesNoType,
} from '@pingum/api-services/EventDataService/EventDataTypes';
import styles from './styles';

export const DEBOUNCE_TIME = 1000;

const isNameValid = (name) => name !== null && name !== undefined && name.length >= 2 && name.length <= 100;

const getConfigurationComponentForEventData = (
  type,
  configuration,
  onChange,
  classes,
) => {
  if (type === textType) {
    return (
      <FormGroup>
        <FormControlLabel
          control={(
            <Checkbox
              checked={configuration.multilineTextField}
              onChange={(e) => {
                onChange({
                  ...configuration,
                  multilineTextField: e.target.checked,
                });
              }}
              value="multilineTextField"
              color="primary"
            />
          )}
          label={(
            <div className={classes.toggleFieldLabelContainer}>
              <Typography className={classes.toggleFieldLabel}>
                Multiline Text Response
              </Typography>
              <Tooltip title="If checked, users will have a large text box to provide a response, otherwise they get a single line text box.">
                <Icon fontSize="small" color="disabled">
                  info_outline
                </Icon>
              </Tooltip>
            </div>
          )}
        />
      </FormGroup>
    );
  }
  if (type === yesNoType) {
    return (
      <FormGroup>
        <FormControlLabel
          control={(
            <Checkbox
              checked={configuration.moreInfoOnYes}
              onChange={(e) => {
                onChange({
                  ...configuration,
                  moreInfoOnYes: e.target.checked,
                });
              }}
              value="moreInfoOnYes"
              color="primary"
            />
          )}
          label={(
            <div className={classes.toggleFieldLabelContainer}>
              <Typography className={classes.toggleFieldLabel}>
                More Info On Yes
              </Typography>
              <Tooltip title="If yes is selected, show a text field for user to provide clarification and more details.">
                <Icon fontSize="small" color="disabled">
                  info_outline
                </Icon>
              </Tooltip>
            </div>
          )}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={configuration.moreInfoOnNo}
              onChange={(e) => {
                onChange({
                  ...configuration,
                  moreInfoOnNo: e.target.checked,
                });
              }}
              value="moreInfoOnNo"
              color="primary"
            />
          )}
          label={(
            <div className={classes.toggleFieldLabelContainer}>
              <Typography className={classes.toggleFieldLabel}>
                More Info On No
              </Typography>
              <Tooltip title="If no is selected, show a text field for user to provide clarification and more details.">
                <Icon fontSize="small" color="disabled">
                  info_outline
                </Icon>
              </Tooltip>
            </div>
          )}
        />
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label="More Information Label"
          value={configuration.moreInfoFieldLabel || ''}
          onChange={(e) => {
            onChange({
              ...configuration,
              moreInfoFieldLabel: e.target.value,
            });
          }}
          helperText="This is the label of the text field shown when more information is requested after selecting yes or no."
        />
      </FormGroup>
    );
  }
  if (type === userType) {
    return (
      <FormGroup>
        <FormControlLabel
          control={(
            <Checkbox
              checked={configuration.notifyUsers}
              onChange={(e) => {
                onChange({
                  ...configuration,
                  notifyUsers: e.target.checked,
                });
              }}
              value="notifyUsers"
              color="primary"
            />
          )}
          label={(
            <div className={classes.toggleFieldLabelContainer}>
              <Typography className={classes.toggleFieldLabel}>
                Notify users
              </Typography>
              <Tooltip title="If checked, users added to this custom field will receive notification for the event.">
                <Icon fontSize="small" color="disabled">
                  info_outline
                </Icon>
              </Tooltip>
            </div>
          )}
        />
      </FormGroup>
    );
  }
  return <span />;
};

export class EventDataEditorComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      validation: {
        name: {
          isValid: true,
          message: '',
        },
      },
      updatedEventData: { },
    };
  }

  setUpdatedEventData = (changes) => {
    this.setState(({ updatedEventData }) => ({
      updatedEventData: {
        ...updatedEventData,
        ...changes,
      },
    }));
  }

  handleClose = () => {
    this.props.onClose();
  }

  handleArchive = () => {
    this.props.editEventData(this.props.eventData.id, { archived: true });
    this.handleClose();
  };

  handleUnarchive = () => {
    this.patchEventData({ archived: false });
    this.handleClose();
  };

  handleSave = () => {
    this.props.editEventData(this.props.eventData.id, this.state.updatedEventData);
    this.handleClose();
  }

  handleNameChange = (e) => {
    const name = e.target.value;
    const isValid = isNameValid(name);
    this.setUpdatedEventData({ name });

    this.setState({
      validation: {
        name: {
          isValid,
          message: !isValid
            ? (
              <span>Please provide a valid field name (between 2 and 100 characters in
                length).
              </span>
            )
            : '',
        },
      },
    });
  }

  handleChangeType = (e) => {
    this.setUpdatedEventData({ type: e.target.value });
  };

  handleChangeRequired = (e) => {
    this.setUpdatedEventData({ required: e.target.checked });
  };

  handleConfigurationChange = (configuration) => {
    this.setUpdatedEventData({ configuration });
  };

  handleChangeDescription = (e) => {
    this.setUpdatedEventData({ description: e.target.value });
  };

  render() {
    const {
      eventData: originalEventData,
      classes,
    } = this.props;
    const {
      validation,
      updatedEventData,
    } = this.state;

    const eventData = {
      ...originalEventData,
      ...updatedEventData,
    };

    const configurationComponent = getConfigurationComponentForEventData(
      eventData.type,
      eventData.configuration,
      this.handleConfigurationChange,
      classes,
    );

    return (
      <Dialog
        open
        onClose={this.handleClose}
        classes={{ paper: classes.dialog }}
      >
        {
          !eventData.id
            ? <DialogTitle>Add New Custom Field</DialogTitle>
            : <DialogTitle>Edit Custom Field</DialogTitle>
        }
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            error={!validation.name.isValid}
            variant="outlined"
            label="Field Name"
            value={eventData.name}
            onChange={this.handleNameChange}
            id={`event-data-name${eventData.id}`}
            classes={{ root: classes.fieldName }}
            helperText={!validation.name.isValid && validation.name.message}
          />

          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel id="type-select-outlined-label">Field Type</InputLabel>
            <Select
              labelId="type-select-outlined-label"
              id="type-select-outlined"
              value={eventData.type}
              onChange={this.handleChangeType}
              labelWidth={80}
            >
              <MenuItem value={addressType}>Address</MenuItem>
              <MenuItem value={dateType}>Date</MenuItem>
              <MenuItem value={dateTimeType}>Datetime</MenuItem>
              <MenuItem value={emailType}>Email</MenuItem>
              <MenuItem value={numberType}>Number</MenuItem>
              <MenuItem value={phoneType}>Phone</MenuItem>
              <MenuItem value={textType}>Text</MenuItem>
              <MenuItem value={userType}>User</MenuItem>
              <MenuItem value={yesNoType}>Yes/No</MenuItem>
            </Select>
          </FormControl>

          <TextField
            id="event-data-description"
            label="Description"
            multiline
            fullWidth
            rows={4}
            variant="outlined"
            margin="normal"
            value={eventData.description || ''}
            onChange={this.handleChangeDescription}
            helperText="Use this field to provide more information about the field or question to the user. This text will show up just below the field."
          />

          <Tooltip title="When added to an event, this field must be filled out in order to create or update the event">
            <FormControlLabel
              control={(
                <Checkbox
                  checked={eventData.required}
                  onChange={this.handleChangeRequired}
                  value="required"
                  color="primary"
                />
              )}
              label="Required"
            />
          </Tooltip>

          {configurationComponent}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose}>
            Cancel
          </Button>
          {
            !!eventData.id
              && !eventData.archived
              && (
                <Button onClick={this.handleArchive} color="inherit" className={classes.archiveButton}>
                  Archive
                </Button>
              )
          }
          {
            !!eventData.id
              && !!eventData.archived
              && (
                <Button onClick={this.handleUnarchive} color="secondary">
                  Restore
                </Button>
              )
          }
          <Button onClick={this.handleSave} color="primary" variant="contained" disabled={!validation.name.isValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

EventDataEditorComponent.propTypes = {
  eventData: PropTypes.object.isRequired,
  editEventData: PropTypes.func.isRequired,
  classes: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

EventDataEditorComponent.defaultProps = { classes: {} };

export default withStyles(styles)(EventDataEditorComponent);
